import type { LocalVideoTrack } from "livekit-client";
import {
    boundScreenShareWait,
    SCREEN_SHARE_MEDIA_TIMEOUT_MS,
    SCREEN_SHARE_POLL_MS,
    waitForScreenShare
} from "./ScreenShareHandover";

/** Decode unpublished capture before asking another device to stop sharing. */
export const prepareScreenShareCapture = async (track: MediaStreamTrack, isCurrent: () => boolean): Promise<void> => {
    const video = document.createElement("video");
    video.muted = true;
    video.playsInline = true;
    video.srcObject = new MediaStream([track]);
    const deadline = performance.now() + SCREEN_SHARE_MEDIA_TIMEOUT_MS;
    try {
        // Do not let a browser play promise hold capture preparation open indefinitely.
        video.play().catch(() => undefined);
        while (performance.now() < deadline) {
            if (!isCurrent() || track.readyState !== "live") {
                throw new Error("Screen capture ended");
            }
            if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA && video.videoWidth > 0 && video.videoHeight > 0) {
                return;
            }
            await waitForScreenShare(SCREEN_SHARE_POLL_MS);
        }
        throw new Error("Screen capture did not produce a frame");
    } finally {
        video.pause();
        video.srcObject = null;
    }
};

export const verifyScreenShareTransmission = async (track: LocalVideoTrack, isCurrent: () => boolean): Promise<void> => {
    const deadline = performance.now() + SCREEN_SHARE_MEDIA_TIMEOUT_MS;
    while (performance.now() < deadline) {
        if (!isCurrent() || track.mediaStreamTrack.readyState !== "live") {
            throw new Error("Screen capture ended");
        }
        // A resolved publish promise alone does not establish that video is being transmitted.
        const report = await boundScreenShareWait(
            track.getRTCStatsReport(), Math.max(1, deadline - performance.now())
        );
        let transmitted = false;
        report?.forEach((stats: RTCOutboundRtpStreamStats) => {
            if (stats.type === "outbound-rtp" && stats.kind === "video" &&
                (stats.framesEncoded || 0) > 0 && (stats.bytesSent || 0) > 0) {
                transmitted = true;
            }
        });
        if (transmitted) {
            return;
        }
        await waitForScreenShare(SCREEN_SHARE_POLL_MS);
    }
    throw new Error("Screen video transmission did not start");
};
